import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_URLS } from "./apis/urls";
import Api from "./utils/apiCall";

interface LanguagesState {
  data: any;
  updateLanguageStatusData: any;
  groupsData: any;
  languageTranslationsData: any;
  addLanguageGroupData: any;
  versionsData: any;
  addLanguageVersionData: any;
  updateLanguageVersionData: any;
  deleteLanguageVersionData: any;
  setDefaultVersionData: any;
  // Translation CRUD operations
  addLanguageTranslationData: any;
  updateLanguageTranslationData: any;
  deleteLanguageTranslationData: any;
  bulkUpdateTranslationsData: any;
  loading: "idle" | "pending" | "fulfilled" | "rejected";
  error: string | null;
  action:
  "add-lang" | "list" | "translation_publish"
  | "update-status"
  | "groups-list"
  | "add-language-group"
  | "translations-list"
  | "add-language-translation"
  | "update-language-translation"
  | "delete-language-translation"
  | "bulk-update-translations"
  | "versions-list"
  | "add-language-version"
  | "update-language-version"
  | "delete-language-version"
  | "set-default-version"
  | null;
}

const languagesInitialState: LanguagesState = {
  data: null,
  updateLanguageStatusData: null,
  groupsData: null,
  languageTranslationsData: null,
  addLanguageGroupData: null,
  versionsData: null,
  addLanguageVersionData: null,
  updateLanguageVersionData: null,
  deleteLanguageVersionData: null,
  setDefaultVersionData: null,
  // Translation CRUD operations
  addLanguageTranslationData: null,
  updateLanguageTranslationData: null,
  deleteLanguageTranslationData: null,
  bulkUpdateTranslationsData: null,
  loading: "idle",
  error: null,
  action: null,
};

export const addLanguages: any = createAsyncThunk(
  "languages/add",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.LANGUAGES.ADD_LANGUAGE,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);


export const publishTranslation: any = createAsyncThunk(
  "translation/publish",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.LANGUAGES.PUBLISH_TRANSLATION,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getLanguages: any = createAsyncThunk(
  "languages/getLanguages",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.LANGUAGES.GET_LANGUAGES,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const updateLanguageStatus: any = createAsyncThunk(
  "languages/updateLanguageStatus",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.LANGUAGES.UPDATE_LANGUAGE_STATUS,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getLanguageGroups: any = createAsyncThunk(
  "languages/getLanguageGroups",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.LANGUAGES.GET_LANGUAGE_GROUPS,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const addLanguageGroup: any = createAsyncThunk(
  "languages/addLanguageGroup",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.LANGUAGES.ADD_LANGUAGE_GROUP,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const getLanguageTranslations: any = createAsyncThunk(
  "languages/getLanguageTranslations",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.LANGUAGES.GET_LANGUAGE_TRANSLATIONS,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// ===== LANGUAGE TRANSLATIONS ASYNC THUNKS =====

export const addLanguageTranslation: any = createAsyncThunk(
  "languages/addLanguageTranslation",
  async (payload: any, thunkAPI) => {
    try {
      // Format according to your API specification
      const requestPayload = {
        language_id: payload.language_id,
        group_id: parseInt(payload.group_id) || 1, // Ensure it's a number
        translation_key: payload.translation_key,
        translation_value: payload.translation_value,
        status:
          payload.status === "1" ||
            payload.status === true ||
            payload.status === 1
            ? true
            : false, // Boolean value
      };

      console.log(
        "🚀 Adding translation with API-compliant payload:",
        requestPayload
      );

      const response = await Api.post(
        API_URLS.LANGUAGES.ADD_LANGUAGE_TRANSLATION,
        requestPayload
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "❌ Add translation API error:",
        error?.response?.data || error
      );
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const updateLanguageTranslation: any = createAsyncThunk(
  "languages/updateLanguageTranslation",
  async (payload: any, thunkAPI) => {
    try {
      // Only send the required fields - exclude language_id if it's causing 400 error
      const requestPayload = {
        id: payload.id,
        translation_key: payload.translation_key,
        translation_value: payload.translation_value,
      };

      console.log("🔄 Updating translation with payload:", requestPayload);

      const response = await Api.post(
        API_URLS.LANGUAGES.UPDATE_LANGUAGE_TRANSLATION,
        requestPayload
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "❌ Update translation API error:",
        error?.response?.data || error
      );
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const deleteLanguageTranslation: any = createAsyncThunk(
  "languages/deleteLanguageTranslation",
  async (payload: any, thunkAPI) => {
    try {
      // Only send the required fields - just the ID for deletion
      const requestPayload = {
        id: payload.id,
      };

      console.log("🗑️ Deleting translation with payload:", requestPayload);

      const response = await Api.post(
        API_URLS.LANGUAGES.DELETE_LANGUAGE_TRANSLATION,
        requestPayload
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "❌ Delete translation API error:",
        error?.response?.data || error
      );
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const bulkUpdateTranslations: any = createAsyncThunk(
  "languages/bulkUpdateTranslations",
  async (payload: any, thunkAPI) => {
    try {
      // Format according to your API specification for bulk update
      const requestPayload = {
        language_id: payload.language_id,
        translations: payload.translations, // Array of translation updates
      };

      console.log(
        "🔄 Bulk updating translations with API-compliant payload:",
        requestPayload
      );

      const response = await Api.post(
        API_URLS.LANGUAGES.UPDATE_LANGUAGE_TRANSLATION,
        requestPayload
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "❌ Bulk update translations API error:",
        error?.response?.data || error
      );
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

// ===== LANGUAGE VERSIONS ASYNC THUNKS =====

export const getLanguageVersions: any = createAsyncThunk(
  "languages/getLanguageVersions",
  async (payload: any, thunkAPI) => {
    try {
      const requestPayload = { ...payload };
      if (payload) {
        if (payload.language !== undefined) {
          requestPayload.language = payload.language;
        }
        if (payload.version !== undefined) {
          requestPayload.version = payload.version;
        }
      }
      const response = await Api.post(
        API_URLS.LANGUAGES.GET_LANGUAGE_VERSIONS,
        requestPayload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const addLanguageVersion: any = createAsyncThunk(
  "languages/addLanguageVersion",
  async (payload: any, thunkAPI) => {
    try {
      // Format the payload to match your API specification exactly
      // Only send the fields that the API expects
      const requestPayload = {
        language_id: payload.language_id,
        version:
          payload.version_code || payload.version || payload.version_name,
        created_by: payload.created_by || "admin",
      };

      console.log("🚀 Sending API request with payload:", requestPayload);

      const response = await Api.post(
        API_URLS.LANGUAGES.ADD_LANGUAGE_VERSION,
        requestPayload
      );
      return response.data;
    } catch (error: any) {
      console.error("❌ API Error:", error?.response?.data || error);
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const updateLanguageVersion: any = createAsyncThunk(
  "languages/updateLanguageVersion",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.LANGUAGES.UPDATE_LANGUAGE_VERSION,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const deleteLanguageVersion: any = createAsyncThunk(
  "languages/deleteLanguageVersion",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.LANGUAGES.DELETE_LANGUAGE_VERSION,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

export const setDefaultLanguageVersion: any = createAsyncThunk(
  "languages/setDefaultLanguageVersion",
  async (payload: any, thunkAPI) => {
    try {
      const response = await Api.post(
        API_URLS.LANGUAGES.SET_DEFAULT_VERSION,
        payload
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(Api.getErrorMessage(error));
    }
  }
);

const languageSlice = createSlice({
  name: "reset",
  initialState: languagesInitialState,
  reducers: {
    resetAll: (state: any) => {
      state.loading = "idle";
      state.error = null;
      state.data = null;
      state.action = null;
    },
  },

  extraReducers: (builder) => {
    builder.addCase(getLanguages.pending, (state) => {
      state.loading = "pending";
      state.data = null;
      state.error = null;
      state.action = "list";
    });
    builder.addCase(getLanguages.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.data = action.payload;
      state.error = null;
      state.action = "list";
    });
    builder.addCase(getLanguages.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.data = null;
      state.action = "list";
    });

    builder.addCase(updateLanguageStatus.pending, (state) => {
      state.loading = "pending";
      state.updateLanguageStatusData = null;
      state.error = null;
      state.action = "update-status";
    });
    builder.addCase(updateLanguageStatus.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.updateLanguageStatusData = action.payload;
      state.error = null;
      state.action = "update-status";
    });
    builder.addCase(updateLanguageStatus.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.updateLanguageStatusData = null;
      state.action = "update-status";
    });

    builder.addCase(getLanguageGroups.pending, (state) => {
      state.loading = "pending";
      state.groupsData = null;
      state.error = null;
      state.action = "groups-list";
    });
    builder.addCase(getLanguageGroups.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.groupsData = action.payload;
      state.error = null;
      state.action = "groups-list";
    });
    builder.addCase(getLanguageGroups.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.groupsData = null;
      state.action = "groups-list";
    });

    builder.addCase(addLanguageGroup.pending, (state) => {
      state.loading = "pending";
      state.addLanguageGroupData = null;
      state.error = null;
      state.action = "add-language-group";
    });
    builder.addCase(addLanguageGroup.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.addLanguageGroupData = action.payload;
      state.error = null;
      state.action = "add-language-group";
    });
    builder.addCase(addLanguageGroup.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.addLanguageGroupData = null;
      state.action = "add-language-group";
    });

    builder.addCase(getLanguageTranslations.pending, (state) => {
      state.loading = "pending";
      state.languageTranslationsData = null;
      state.error = null;
      state.action = "translations-list";
    });
    builder.addCase(getLanguageTranslations.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.languageTranslationsData = action.payload.data;
      state.error = null;
      state.action = "translations-list";
    });
    builder.addCase(getLanguageTranslations.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.languageTranslationsData = null;
      state.action = "translations-list";
    });

    // ===== LANGUAGE TRANSLATIONS CRUD REDUCERS =====

    // Add Language Translation
    builder.addCase(addLanguageTranslation.pending, (state) => {
      state.loading = "pending";
      state.addLanguageTranslationData = null;
      state.error = null;
      state.action = "add-language-translation";
    });
    builder.addCase(addLanguageTranslation.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.addLanguageTranslationData = action.payload;
      state.error = null;
      state.action = "add-language-translation";
    });
    builder.addCase(addLanguageTranslation.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.addLanguageTranslationData = null;
      state.action = "add-language-translation";
    });

    // Update Language Translation
    builder.addCase(updateLanguageTranslation.pending, (state) => {
      state.loading = "pending";
      state.updateLanguageTranslationData = null;
      state.error = null;
      state.action = "update-language-translation";
    });
    builder.addCase(updateLanguageTranslation.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.updateLanguageTranslationData = action.payload;
      state.error = null;
      state.action = "update-language-translation";
    });
    builder.addCase(updateLanguageTranslation.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.updateLanguageTranslationData = null;
      state.action = "update-language-translation";
    });

    // Delete Language Translation
    builder.addCase(deleteLanguageTranslation.pending, (state) => {
      state.loading = "pending";
      state.deleteLanguageTranslationData = null;
      state.error = null;
      state.action = "delete-language-translation";
    });
    builder.addCase(deleteLanguageTranslation.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.deleteLanguageTranslationData = action.payload;
      state.error = null;
      state.action = "delete-language-translation";
    });
    builder.addCase(deleteLanguageTranslation.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.deleteLanguageTranslationData = null;
      state.action = "delete-language-translation";
    });

    // Bulk Update Translations
    builder.addCase(bulkUpdateTranslations.pending, (state) => {
      state.loading = "pending";
      state.bulkUpdateTranslationsData = null;
      state.error = null;
      state.action = "bulk-update-translations";
    });
    builder.addCase(bulkUpdateTranslations.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.bulkUpdateTranslationsData = action.payload;
      state.error = null;
      state.action = "bulk-update-translations";
    });
    builder.addCase(bulkUpdateTranslations.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.bulkUpdateTranslationsData = null;
      state.action = "bulk-update-translations";
    });

    // ===== LANGUAGE VERSIONS REDUCERS =====

    // Get Language Versions
    builder.addCase(getLanguageVersions.pending, (state) => {
      state.loading = "pending";
      state.versionsData = null;
      state.error = null;
      state.action = "versions-list";
    });
    builder.addCase(getLanguageVersions.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.versionsData = action.payload;
      state.error = null;
      state.action = "versions-list";
    });
    builder.addCase(getLanguageVersions.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.versionsData = null;
      state.action = "versions-list";
    });

    // Add Language Version
    builder.addCase(addLanguageVersion.pending, (state) => {
      state.loading = "pending";
      state.addLanguageVersionData = null;
      state.error = null;
      state.action = "add-language-version";
    });
    builder.addCase(addLanguageVersion.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.addLanguageVersionData = action.payload;
      state.error = null;
      state.action = "add-language-version";
    });
    builder.addCase(addLanguageVersion.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.addLanguageVersionData = null;
      state.action = "add-language-version";
    });

    // Update Language Version
    builder.addCase(updateLanguageVersion.pending, (state) => {
      state.loading = "pending";
      state.updateLanguageVersionData = null;
      state.error = null;
      state.action = "update-language-version";
    });
    builder.addCase(updateLanguageVersion.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.updateLanguageVersionData = action.payload;
      state.error = null;
      state.action = "update-language-version";
    });
    builder.addCase(updateLanguageVersion.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.updateLanguageVersionData = null;
      state.action = "update-language-version";
    });

    // Delete Language Version
    builder.addCase(deleteLanguageVersion.pending, (state) => {
      state.loading = "pending";
      state.deleteLanguageVersionData = null;
      state.error = null;
      state.action = "delete-language-version";
    });
    builder.addCase(deleteLanguageVersion.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.deleteLanguageVersionData = action.payload;
      state.error = null;
      state.action = "delete-language-version";
    });
    builder.addCase(deleteLanguageVersion.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.deleteLanguageVersionData = null;
      state.action = "delete-language-version";
    });

    // Set Default Language Version
    builder.addCase(setDefaultLanguageVersion.pending, (state) => {
      state.loading = "pending";
      state.setDefaultVersionData = null;
      state.error = null;
      state.action = "set-default-version";
    });
    builder.addCase(setDefaultLanguageVersion.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.setDefaultVersionData = action.payload;
      state.error = null;
      state.action = "set-default-version";
    });
    builder.addCase(setDefaultLanguageVersion.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.setDefaultVersionData = null;
      state.action = "set-default-version";
    });



    builder.addCase(addLanguages.pending, (state) => {
      state.loading = "pending";
      state.error = null;
      state.action = "add-lang";
    });
    builder.addCase(addLanguages.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.error = null;
      state.action = "add-lang";
    });
    builder.addCase(addLanguages.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.action = "add-lang";
    });


    builder.addCase(publishTranslation.pending, (state) => {
      state.loading = "pending";
      state.error = null;
      state.action = "translation_publish";
    });
    builder.addCase(publishTranslation.fulfilled, (state, action) => {
      state.loading = "fulfilled";
      state.error = null;
      state.action = "translation_publish";
    });
    builder.addCase(publishTranslation.rejected, (state, action) => {
      state.loading = "rejected";
      state.error = action.payload as string;
      state.action = "translation_publish";
    });





  },
});

export const { resetAll } = languageSlice.actions;
export default languageSlice.reducer;
